import { useEffect, useState } from "react";
import { Form, Input, Modal, Select, Table } from "antd";
import notifier from "utils/notifier";
import { EMCodeSelectionModalProps, OtherProviderModalProps, RelatedNdcModalProps } from "./Proptypes";
import { validateNDC } from "./Validators";

export const OtherProviderModal = (props: OtherProviderModalProps) => {

  const columns = [
    {
      title: 'NPI',
      key: 'NpiId',
      dataIndex: 'NpiId',
      sorter: (a: any, b: any) => a.NpiId.localeCompare(b.NpiId),
    },
    {
      title: 'PROVIDER ID',
      key: 'Id',
      dataIndex: 'Id',
      sorter: (a: any, b: any) => a.Id.localeCompare(b.Id),
    },
    {
      title: 'PROVIDER',
      key: 'ProviderName',
      dataIndex: 'ProviderName',
      sorter: (a: any, b: any) => a.ProviderName.localeCompare(b.ProviderName),
    },

  ]

  return (
    <Modal
      {...props}
      title="Please select the primary rendering provider."
      footer={null}
      maskClosable={true}
    >
      <Table
        className="rendering-provider-table"
        columns={columns}
        dataSource={props.otherProviders}
        rowKey={'Id'}
        onRow={record => {
          return {
            onClick: () => props.clickOtherProvider(record.NpiId.trim()), // click row
          };
        }}
        loading={props.isFetching}
      />

    </Modal>
  );
};

export const RelatedNdcModal = (props: RelatedNdcModalProps) => {

  const [searchValue, setSearchValue] = useState('');
  const [dataSource, setDataSource] = useState<any>([]);
  const [form] = Form.useForm();
  const [selectedItem, setSelectedItem] = useState('none');
  const [rowKeys, setRowKeys] = useState<any>([]);

  useEffect(() => {
    setDataSource(props.relatedNdc);
  }, [props.relatedNdc]);

  const onSubmit = () => {
    if (selectedItem !== 'none') {
      form.validateFields().then(values => {
        props.handleSubmit(values, selectedItem);
        setSelectedItem('none');
        setRowKeys([]);
        form.resetFields();
      })
    } else {
      notifier.error('Please select an NDC from the list.');
    }
  };

  const columns = [
    {
      title: 'NDC',
      key: 'RmiNDC',
      dataIndex: 'RmiNDC',
      sorter: (a: any, b: any) => a.RmiNDC.localeCompare(b.RmiNDC),
      render: (text: string) => text === 'XX' ?
        <Form.Item
          name={'ndcCode'}
          rules={[
            { required: selectedItem === 'input', message: 'Enter a Code.' },
            { validator: validateNDC }
          ]}
        >
          <Input
            type="number"
            onKeyDown={ (evt) => (evt.key === 'e' || evt.key === '+' || evt.key === '-' || evt.key === '.') && evt.preventDefault() }
            onChange={e =>  {
              form.setFieldsValue({ ndcCode: e.target.value.slice(0, e.target.maxLength) })
            }}
            onPaste={event => {
              const pasteContent = event.clipboardData.getData('text');
              const modified = pasteContent.replace(/\D/g, '');;

              event.preventDefault();

              form.setFieldsValue({ ndcCode: modified.slice(0, 11) })
            }}
            maxLength={11}
          />
        </Form.Item>
          : text,
    },
    {
      title: 'Drug Name',
      key: 'RmiDrugName',
      dataIndex: 'RmiDrugName',
      sorter: (a: any, b: any) => a.RmiDrugName.localeCompare(b.RmiDrugName),
      render: (text: string, record: any) => record.RmiNDC === 'XX' ? 'Please enter valid NDC. NDC codes must be in 11-digital numeric format with no special characters and must convert 10-digit to 11-digit NDC format.' : text
    },
    {
      title: 'Bill Units',
      key: 'RmiBillUnits',
      dataIndex: 'RmiBillUnits',
    },
    {
      title: 'Bill Units per Pkg',
      key: 'RmiBillUnitsPerPkg',
      dataIndex: 'RmiBillUnitsPerPkg',
    },
    {
      title: 'Pkg Size',
      key: 'RmiPkgSize',
      dataIndex: 'RmiPkgSize',
    },
    {
      title: 'Pkg UOM',
      key: 'RmiPkgUom',
      dataIndex: 'RmiPkgUom',
    },
    {
      title: 'Pkg Qty',
      key: 'RmiPkgQty',
      dataIndex: 'RmiPkgQty',
    },
    {
      title: 'Pkg Desc',
      key: 'RmiPkgDesc',
      dataIndex: 'RmiPkgDesc',
    },
  ];

  const rowSelection = {
    selectedRowKeys: rowKeys,
    onChange: (selectedKeys: React.Key[], selectedRows: any[]) => {
      setRowKeys(selectedKeys);
      if (selectedRows[0].RmiNDC !== 'XX') {
         props.setSelectedRelatedNdc(selectedRows[0]);
         setSelectedItem('item');
      }
      else {
        setSelectedItem('input');
      }
    },
    getCheckboxProps: (record: any) => ({
      disabled: record.RmiNDC === 'search', // Column configuration not to be checked
      name: record.RmiNDC,
    }),
  };

  return (
    <Modal
      {...props}
      title={'Select Related NDC for ' + props.selectedCPT}
      maskClosable={false}
      cancelButtonProps={{ style: { display: 'none' }}}
      onOk={onSubmit}
      closable={false}
    >
      <Input
        placeholder="Search Drug Name"
        value={searchValue}
        width={'100%'}
        onChange={e => {
          const currValue = e.target.value;
          setSearchValue(currValue);
          const filteredData = props.relatedNdc.filter((entry: any) =>
            entry.RmiDrugName.toLowerCase().includes(currValue.toLowerCase()) ||
            entry.RmiNDC.includes(currValue)
          );
          setDataSource(filteredData);
        }}
      />
      <Form
      form={form}
      name={'relatedNDCModal'}
      onFinish={onSubmit}
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey={'RmiNDC'}
          rowSelection={{
            type: 'radio',
            ...rowSelection,
          }}
          loading={props.isFetching}
          pagination={{ showSizeChanger: true, hideOnSinglePage: true }}
        />
      </Form>


    </Modal>
  )
};

export const EMCodeSelectionModal = ({ EMCodeList, selectedEMCode, setSelectedEMCode, ...rest }: EMCodeSelectionModalProps) => {

  return (
    <Modal
      {...rest}
      title={'E&M Code Selection'}
      maskClosable={false}
      okButtonProps={{
        disabled: selectedEMCode === ''
      }}
    >
      <div>
        The claim you are submitting contains measures that require an E&M Code. Please select one below.
      </div>
      <br />
      <Select
        style={{ width: '100%' }}
        placeholder="Please Select an E&M Code..."
        onSelect={(val: string) => setSelectedEMCode(val)}
      >
        {EMCodeList.map(value => (
          <Select.Option key={value.Code} value={value.Code}>
            {value.Code}: {value.Description}
          </Select.Option>
        ))}
      </Select>
    </Modal>
  )
};